import {
  useState, useCallback, useMemo 
} from 'react';
import {
  useParams, useNavigate 
} from 'react-router-dom';
import {
  ArrowLeft, Camera, FileText, ClipboardCheck, Package,
  AlertCircle, Loader2, QrCode, Plus, Layers, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CameraCapture } from '@/components/ui/camera-capture';
import { QRScanner } from '@/components/ui/qr-scanner';
import {
  AIAnalysisPanel, ImageQueue, ConsolidatedReport,
} from '@/features/receiving/components';
import { useDeliveryByPO } from '@/features/receiving/hooks/use-delivery-by-po';
import { useReceivingAI } from '@/features/receiving/hooks/use-receiving-ai';
import { useImageQueue } from '@/features/receiving/hooks/use-image-queue';
import { cn } from '@/lib/classnames';
import {
  ReviewTab, QueueModeSelect, ChecklistTab,
} from './components';
import type {
  AnalysisMode, InvoiceAnalysisResult, InvoiceItem, MatchedLineItem,
} from '@/features/receiving/types/ai-analysis';
import type { DeliveryLine } from '@/features/receiving/types';

type ViewMode = 'checklist' | 'camera' | 'invoice' | 'review' | 'queue';

const TABS = [
  {
    id: 'checklist' as const,
    label: 'Checklist',
    icon: ListChecks,
  },
  {
    id: 'camera' as const,
    label: 'Count Boxes',
    icon: Camera 
  },
  {
    id: 'invoice' as const,
    label: 'Scan Invoice',
    icon: FileText 
  },
  {
    id: 'queue' as const,
    label: 'Multi-Image',
    icon: Layers 
  },
  {
    id: 'review' as const,
    label: 'Review',
    icon: ClipboardCheck 
  },
];

function getStatusBadgeClass(status: string): string {
  if (status === 'completed') return 'bg-green-100 text-green-800';
  if (status === 'in-progress') return 'bg-amber-100 text-amber-800';
  return 'bg-blue-100 text-blue-800';
}

function matchInvoiceToDeliveryLines(
  invoiceItems: InvoiceItem[],
  deliveryLines: DeliveryLine[]
): MatchedLineItem[] {
  const matches: MatchedLineItem[] = [];
  for (const invoiceItem of invoiceItems) {
    const skuMatch = deliveryLines.find(
      (line) => line.sku && line.sku.toLowerCase() === invoiceItem.sku?.toLowerCase()
    );
    const nameMatch = skuMatch ? null : deliveryLines.find((line) =>
      line.itemName.toLowerCase().includes(invoiceItem.productName.toLowerCase()) ||
      invoiceItem.productName.toLowerCase().includes(line.itemName.toLowerCase())
    );
    const matchedLine = skuMatch ?? nameMatch;
    if (matchedLine) {
      matches.push({
        deliveryLineId: matchedLine.lineId,
        invoiceItem,
        matchConfidence: skuMatch ? 0.95 : 0.7,
        matchReason: skuMatch ? 'sku' : 'name',
        suggestedQuantity: invoiceItem.quantity,
        suggestedLotNumber: invoiceItem.lotNumber,
        suggestedExpiryDate: invoiceItem.expiryDate,
      });
    }
  }
  return matches;
}

export function GoodsReceivingPage() {
  const { poNumber } = useParams<{ poNumber: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ViewMode>('checklist');
  const [showQRScanner, setShowQRScanner] = useState(poNumber === undefined);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('box-counting');
  const [matchedLines, setMatchedLines] = useState<MatchedLineItem[]>([]);
  const [queueMode, setQueueMode] = useState<AnalysisMode>('box-counting');

  const {
    data: delivery, isLoading, error: deliveryError 
  } = useDeliveryByPO(poNumber);

  const handleAnalysisComplete = useCallback((result: unknown, mode: AnalysisMode) => {
    if (mode === 'invoice' && delivery) {
      const invoiceResult = result as InvoiceAnalysisResult;
      setMatchedLines(matchInvoiceToDeliveryLines(invoiceResult.items, delivery.lines));
    }
  }, [delivery]);

  const {
    isAnalyzing, streamingText, result, error: aiError, analyzeImage, reset: resetAI,
  } = useReceivingAI({
    delivery,
    onAnalysisComplete: handleAnalysisComplete 
  });

  const handleQueueComplete = useCallback((report: unknown) => {
    const typedReport = report as { invoiceData?: { items: InvoiceItem[] } };
    if (typedReport.invoiceData && delivery) {
      setMatchedLines(matchInvoiceToDeliveryLines(typedReport.invoiceData.items, delivery.lines));
    }
  }, [delivery]);

  const {
    images: queuedImages, isProcessing: isQueueProcessing, currentProcessingId,
    consolidatedReport, addImage, removeImage, clearQueue, processQueue,
  } = useImageQueue({
    delivery,
    onQueueComplete: handleQueueComplete 
  });

  const handleQRScan = useCallback((data: string) => {
    const poPattern = /\/receiving\/goods\/([A-Z0-9-]+)/i;
    const matchResult = poPattern.exec(data);
    navigate(`/receiving/goods/${matchResult ? matchResult[1] : data}`, { replace: true });
    setShowQRScanner(false);
  }, [navigate]);

  const handleCapture = useCallback((imageBase64: string, mediaType: 'image/jpeg' | 'image/png') => {
    setCapturedImage(imageBase64);
    const mode: AnalysisMode = activeTab === 'invoice' ? 'invoice' : 'box-counting';
    setAnalysisMode(mode);
    analyzeImage(imageBase64, mediaType, mode);
  }, [activeTab, analyzeImage]);

  const handleQueueCapture = useCallback((imageBase64: string, mediaType: 'image/jpeg' | 'image/png') => {
    addImage(imageBase64, mediaType, queueMode);
  }, [addImage, queueMode]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    resetAI();
  }, [resetAI]);

  const handleTabChange = useCallback((tab: ViewMode) => {
    setActiveTab(tab);
    if (tab !== 'review' && tab !== 'queue') {
      setCapturedImage(null);
      resetAI();
    }
  }, [resetAI]);

  const receivingProgress = useMemo(() => {
    if (!delivery) return {
      received: 0,
      total: 0,
      percentage: 0 
    };
    const received = delivery.lines.filter((l) => l.status === 'received').length;
    const total = delivery.lines.length;
    return {
      received,
      total,
      percentage: total > 0 ? Math.round((received / total) * 100) : 0 
    };
  }, [delivery]);

  if (showQRScanner) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-lg mx-auto">
          <div className="mb-6">
            <Button variant="ghost" onClick={() => navigate('/receiving')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Receiving
            </Button>
          </div>
          <Card className="p-6">
            <div className="text-center mb-6">
              <QrCode className="w-12 h-12 mx-auto mb-3 text-blue-600" />
              <h1 className="text-xl font-semibold">Scan Delivery QR Code</h1>
              <p className="text-gray-500 mt-1">Point your camera at the QR code on the delivery</p>
            </div>
            <QRScanner
              onScan={handleQRScan}
              onCancel={() => navigate('/receiving')}
              expectedPattern={/PO-\d{4}-\d{4}/}
              placeholder="Enter PO number (e.g., PO-2026-0002)"
            />
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
          <p className="text-gray-600">Loading delivery {poNumber}...</p>
        </div>
      </div>
    );
  }

  if (deliveryError || !delivery) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-lg mx-auto">
          <Button variant="ghost" onClick={() => navigate('/receiving')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Receiving
          </Button>
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
            <h2 className="text-lg font-semibold mb-2">Delivery Not Found</h2>
            <p className="text-gray-600 mb-4">Could not find a delivery for PO {poNumber}</p>
            <Button onClick={() => setShowQRScanner(true)}>
              <QrCode className="w-4 h-4 mr-2" />Scan Another QR Code
            </Button>
          </Card>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/receiving')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-semibold">{delivery.poNumber}</h1>
                <p className="text-sm text-gray-500">{delivery.supplierName}</p>
              </div>
            </div>
            <span className={cn('px-2 py-1 text-xs font-medium rounded-full', getStatusBadgeClass(delivery.status))}>
              {delivery.status}
            </span>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">Receiving Progress</span>
              <span className="font-medium">{receivingProgress.received}/{receivingProgress.total} items</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 transition-all" style={{ width: `${receivingProgress.percentage}%` }} />
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex border-b -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4">
        {activeTab === 'checklist' && (
          <ChecklistTab
            delivery={delivery}
            onUpdateLine={(lineId, updates) => {
              // Placeholder - will be connected to updateDeliveryLines API
              console.log('Update line', lineId, updates);
            }}
            onReportIssue={(lineId, issue) => {
              // Placeholder - will be connected to discrepancy reporting API
              console.log('Report issue', lineId, issue);
            }}
          />
        )}

        {(activeTab === 'camera' || activeTab === 'invoice') && (
          <div className="space-y-4">
            {capturedImage ? (
              <Card className="overflow-hidden">
                <div className="relative">
                  <img src={`data:image/jpeg;base64,${capturedImage}`} alt="Captured" className="w-full aspect-video object-cover" />
                  <Button variant="outline" size="sm" onClick={handleRetake} className="absolute top-4 right-4 bg-white/90">Retake</Button>
                </div>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <CameraCapture onCapture={handleCapture} aspectRatio="landscape" />
              </Card>
            )}
            {capturedImage && (
              <AIAnalysisPanel mode={analysisMode} isAnalyzing={isAnalyzing} streamingText={streamingText} result={result} error={aiError} onRetry={handleRetake} />
            )}
            {!capturedImage && (
              <Card className="p-4">
                <div className="flex items-start gap-3">
                  {activeTab === 'camera' ? <Package className="w-5 h-5 text-blue-600 mt-0.5" /> : <FileText className="w-5 h-5 text-blue-600 mt-0.5" />}
                  <div>
                    <h3 className="font-medium">{activeTab === 'camera' ? 'Count Boxes' : 'Scan Invoice'}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {activeTab === 'camera'
                        ? 'Take a photo of the delivery to automatically count boxes and pallets.'
                        : 'Take a photo of the invoice or packing slip to extract product details.'}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'queue' && (
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <Layers className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium">Multi-Image Analysis</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Capture multiple images and process them all at once for a consolidated report.
                  </p>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Select image type:</p>
                <QueueModeSelect selectedMode={queueMode} onModeChange={setQueueMode} />
              </div>
              <CameraCapture onCapture={handleQueueCapture} aspectRatio="landscape" />
              <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Plus className="w-4 h-4" />
                <span>Images will be added to the queue below</span>
              </div>
            </Card>
            <ImageQueue
              images={queuedImages}
              onRemove={removeImage}
              onClear={clearQueue}
              onProcessAll={processQueue}
              isProcessing={isQueueProcessing}
              currentProcessingId={currentProcessingId}
            />
            {consolidatedReport && <ConsolidatedReport report={consolidatedReport} />}
          </div>
        )}

        {activeTab === 'review' && (
          <ReviewTab
            delivery={delivery}
            matchedLines={matchedLines}
            receivingProgress={receivingProgress}
            onScanInvoice={() => setActiveTab('invoice')}
          />
        )}
      </main>
    </div>
  );
}
